import type { SketchData } from "@/types/project-legacy-types";

export type PageType = "home" | "slug" | "component";

export interface Annotation {
  readonly id: string;
  readonly text: string;
  readonly created: Date;
}

export interface PageChild {
  readonly id: string;
  readonly type: string;
  readonly props: Record<string, unknown>;
}

export interface PageDomainData {
  readonly sketches: Record<string, SketchData>;
  readonly annotations: Record<string, Annotation>;
  readonly properties: Record<string, unknown>;
  readonly children: Record<string, PageChild>;
}

export interface PageDomain {
  readonly id: string;
  readonly name: string;
  readonly version: string;
  readonly draft: boolean;
  readonly type: PageType;
  readonly description?: string;
  readonly data: PageDomainData;
}

export interface CreatePageInput {
  readonly name: string;
  readonly type: PageType;
  readonly description?: string;
  readonly draft?: boolean;
  readonly version?: string;
}

const generateId = (): string =>
  Date.now().toString(36) + Math.random().toString(36).slice(2);

export const createPage = (input: CreatePageInput): PageDomain => {
  const { name, type, description, draft = false, version = "v1" } = input;

  const page: PageDomain = {
    id: generateId(),
    name,
    version,
    draft,
    type,
    ...(description ? { description } : {}),
    data: {
      sketches: {},
      annotations: {},
      properties: {},
      children: {},
    },
  };

  return page;
};

export const serializePage = (page: PageDomain): string => JSON.stringify(page);

export const deserializePage = (json: string): PageDomain => {
  const raw = JSON.parse(json);
  return {
    ...raw,
    data: {
      sketches: raw.data?.sketches ?? {},
      annotations: raw.data?.annotations ?? {},
      properties: raw.data?.properties ?? {},
      children: raw.data?.children ?? {},
    },
  } as PageDomain;
};
